import { AppGlobalActions } from '@app/_store/actions/global.actions';
import { UnloadedContainer } from '@core/models/general-types/general-types';
import {
  BillsharkBill,
  BillsharkInfo,
  BillsharkSubmitBillResponse,
} from '@core/models/interfaces/storage-data/billshark';
import { createFeature, createReducer, on } from '@ngrx/store';

import { BillsharkInfoActions } from './billshark-info.actions';

export interface BillsharkInfoState extends UnloadedContainer<BillsharkInfo> {
  lastSubmittedBill: BillsharkSubmitBillResponse | null;
}

export const initialBillsharkInfoState: BillsharkInfoState = {
  collection: [],
  object: null,
  metaData: null,
  lastSubmittedBill: null,
};

function convertSubmitResponseToBill(
  response: BillsharkSubmitBillResponse
): BillsharkBill {
  return {
    id: response.id,
    sortKey: Date.now(), // New bills should appear first
    amount: response.amount.toString(),
    providerId: response.providerId,
    providerName: response.providerName,
    providerIcon: response.providerIcon,
    inputType: response.inputType,
    isDismissed: false,
    type: 'Negotiation', // Default, will be updated on sync
    stage: response.stage,
    status: response.status,
    acceptedBillCell: response.acceptedBillCell,
  };
}

const billsharkInfoReducer = createReducer(
  initialBillsharkInfoState,
  on(AppGlobalActions.loadAppDataSuccess, (state, { globalState }) =>
    // R37. `...(globalState.billsharkInfo ?? {})` produced a new state object even
    // when this container was not part of the response, so a one-container
    // load handed all 23 feature slices a new reference and nothing else.
    globalState.billsharkInfo
      ? { ...state, ...globalState.billsharkInfo }
      : state
  ),
  on(
    BillsharkInfoActions.loadBillsharkInfoSuccess,
    (state, { billsharkInfo }) => ({
      ...state,
      ...billsharkInfo,
    })
  ),
  on(BillsharkInfoActions.addSubmittedBill, (state, { submittedBill }) => ({
    ...state,
    lastSubmittedBill: submittedBill,
    collection: [
      convertSubmitResponseToBill(submittedBill),
      ...state.collection.filter((bill) => bill.id !== submittedBill.id),
    ],
  }))
);

export const billsharkInfoFeature = createFeature({
  name: 'billsharkInfo',
  reducer: billsharkInfoReducer,
  extraSelectors: ({ selectBillsharkInfoState }) => ({
    selectBillsharkInfoState,
  }),
});
